import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enTranslations from './locales/en.json';
import { DEFAULT_LANGUAGE, LANGUAGES } from '../seo/seoData';

// English ships in the main bundle as the fallback; other languages are separate chunks
// fetched on demand so adding a language doesn't grow the initial download.
const localeLoaders = import.meta.glob<{ default: Record<string, unknown> }>(['./locales/*.json', '!./locales/en.json']);

const LANGUAGE_STORAGE_KEY = 'i18nextLng';

export const isSupportedLanguage = (lang?: string): lang is string =>
  !!lang && LANGUAGES.includes(lang);

// The language in the URL always wins so the first render matches the prerendered HTML.
// Without one (e.g. "/"), fall back to the last used language, then the browser language.
export const detectLanguage = (): string => {
  const fromPath = window.location.pathname.split('/')[1];
  if (isSupportedLanguage(fromPath)) return fromPath;

  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (isSupportedLanguage(stored ?? undefined)) return stored as string;
  } catch {
    // Storage may be unavailable (private mode); ignore.
  }

  const browser = navigator.language?.slice(0, 2).toLowerCase();
  return isSupportedLanguage(browser) ? browser : DEFAULT_LANGUAGE;
};

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        translation: enTranslations
      }
    },
    lng: DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    interpolation: {
      escapeValue: false
    }
  });

/** Loads the translations for `lang` (if needed) and switches to it. */
export const loadLanguage = async (lang: string) => {
  if (!i18n.hasResourceBundle(lang, 'translation')) {
    const loader = localeLoaders[`./locales/${lang}.json`];
    if (loader) {
      const messages = await loader();
      i18n.addResourceBundle(lang, 'translation', messages.default, true, true);
    }
  }
  if (i18n.language !== lang) await i18n.changeLanguage(lang);
  // Saved here rather than in a languageChanged listener, which would also fire for the
  // initial English fallback and overwrite the user's choice.
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    // Storage may be unavailable (private mode); ignore.
  }
};

export default i18n;
